// PhoneNumberInput.tsx
import { useState, useCallback, useMemo, forwardRef } from "react";
import { LitOutlinedTextField, LitFilledTextField } from "@/components/TextField/LitTextField";
import { parsePhoneNumberFromString, AsYouType } from "libphonenumber-js";

const _formatPhoneNumber = (input: string) => {
  const asYouType = new AsYouType();
  asYouType.input(input);
  return asYouType.getNumber() ? asYouType.getNumber()?.formatInternational() : input;
};

type Props = {
  value: string;
  error?: boolean;
  errorText?: string;
  onChange: (value: string) => void;
  onError?: (error: string | null) => void;
  disabled?: boolean;
  variant?: "outlined" | "filled";
  label?: string;
};

export default forwardRef<HTMLInputElement, Props>(function E164PhoneNumberInput(
  { value = "", error, errorText, onChange, onError, disabled = false, variant = "outlined", label = "Phone Number" },
  ref
) {
  // Split initial value into number and extension
  const [initialNumber, initialExtension] = value.split(";");
  const [phoneNumber, setPhoneNumber] = useState(_formatPhoneNumber(initialNumber) || "");
  const [extension, setExtension] = useState(initialExtension || "");

  // Memoize the phone number parser
  const phoneNumberObj = useMemo(() => parsePhoneNumberFromString(phoneNumber), [phoneNumber]);

  const handleNumberChange = useCallback(
    (e: any) => {
      const inputValue = e.target.value;
      let formattedValue = inputValue;

      // If input starts with a +, use AsYouType for formatting
      if (inputValue.startsWith("+")) {
        formattedValue = _formatPhoneNumber(inputValue);
      }

      setPhoneNumber(formattedValue);

      const parsed = parsePhoneNumberFromString(formattedValue);
      if (parsed?.isValid()) {
        const newValue = extension ? `${parsed.number};${extension}` : parsed.number;
        onChange(newValue);
        onError?.(null);
      } else if (inputValue.length === 1 && !inputValue.startsWith("+")) {
        setPhoneNumber(`+1${inputValue}`);
        onChange("");
        onError?.(null);
      } else if (inputValue.length === 0) {
        setPhoneNumber("");
        setExtension("");
        onChange("");
        onError?.(null);
      } else {
        onError?.("Please enter a valid phone number");
      }
    },
    [extension, onChange, onError]
  );

  const handleExtensionChange = useCallback(
    (e: any) => {
      const newExtension = e.target.value as string;

      setExtension(newExtension);

      if (phoneNumberObj?.isValid()) {
        const newValue = newExtension ? `${phoneNumberObj.number};${newExtension}` : phoneNumberObj.number;
        onChange(newValue);
      }
    },
    [phoneNumberObj, onChange]
  );

  const LitTextField = variant === "outlined" ? LitOutlinedTextField : LitFilledTextField;

  return (
    <div className="flex flex-col">
      <div className="flex gap-4">
        <LitTextField
          ref={ref as any}
          type="tel"
          className="flex-grow"
          label={label}
          value={phoneNumber}
          onInput={handleNumberChange}
          disabled={disabled}
          error={error}
          errorText={errorText}
        />
        <div className="w-32">
          <LitTextField
            type="tel"
            label="Ext"
            value={extension}
            onInput={handleExtensionChange}
            disabled={disabled || !phoneNumberObj?.isValid()}
            error={error}
          />
        </div>
      </div>
    </div>
  );
});
